// Add to Slack (#937): the install route the console sends a person to, and the result the
// callback sends them back with.
//
// Read off `control_plane/routes/slack.py`: `install` answers a signed-in browser with a
// redirect to Slack's consent page, and `callback` answers every outcome with a 303 to the
// console's Integrations page carrying only `slack_install` (and, once installed, the
// installation's id). The code, the state and the token never reach the browser's address.

/** Where Add to Slack goes: a navigation, not a fetch, because Slack's consent page is a page. */
export const SLACK_INSTALL_PATH = "/api/v1/slack/oauth/install";

/**
 * Where Reinstall goes (#938): the install route, asked to replace this installation. The
 * intent is held on the install's state by the server; nothing the browser carries back
 * from Slack can make a plain install a replace.
 */
export const reinstallHref = (installationId: string): string =>
  `${SLACK_INSTALL_PATH}?${new URLSearchParams({ replace: installationId }).toString()}`;

/** The query parameters the callback returns with. */
export const SLACK_INSTALL_PARAM = "slack_install";
export const SLACK_INSTALLATION_PARAM = "installation_id";

/** What became of an install, as the callback says it. */
export type SlackInstallResult =
  | { word: "installed"; installationId: string }
  /** A plain install of a workspace this one already holds (#938): nothing was written. */
  | { word: "already_installed"; installationId: string }
  /** A Reinstall that replaced this installation's token and scopes in place (#938). */
  | { word: "replaced"; installationId: string }
  /** A Reinstall Slack answered for another workspace than the one it replaces (#938). */
  | { word: "other_slack_team" }
  /** Slack's consent declined, or its code refused. */
  | { word: "refused" }
  /** The workspace is another Torque Assist workspace's. */
  | { word: "conflict" }
  /** The install's state was not this session's to redeem: spent, expired or someone else's. */
  | { word: "expired" };

const REFUSALS = ["refused", "conflict", "expired", "other_slack_team"] as const;

/** The results that name an installation, and mean nothing without one. */
const NAMING = ["installed", "already_installed", "replaced"] as const;

/** The result an address carries, or null when it carries none, or one this console cannot read. */
export function slackInstallResultOf(search: string): SlackInstallResult | null {
  const params = new URLSearchParams(search);
  const word = params.get(SLACK_INSTALL_PARAM);
  const naming = NAMING.find((each) => each === word);
  if (naming !== undefined) {
    const installationId = params.get(SLACK_INSTALLATION_PARAM) ?? "";
    return installationId === "" ? null : { word: naming, installationId };
  }
  const refusal = REFUSALS.find((each) => each === word);
  return refusal === undefined ? null : { word: refusal };
}
